import React, { memo } from 'react';
import { Card } from '../common/Card';
import { Heading, Text, Typography } from '../common/Typography';
import { StatusBadge, InfoHintButton } from '../common';
import { getGenderTextColor } from '../../utils/genderColors';
import { formatPlayerName } from '../../utils/nameUtils';
import { formatRankingRulesForDisplay, isTeamWithdrawn } from '../../utils/tournamentStandings';
import { formatTournamentEntryLabel } from '../../utils/tournamentFormat';

/**
 * Mini league table for a single group
 */
const GroupStandingsTable = memo(
  ({ groupName, standings, teamsQualifyingPerGroup, elevated, hasWalkoverOrForfeit, isSinglesTournament, walkoverPointsFor = 21 }) => {
    return (
      <Card variant="muted" size="medium" className={elevated ? 'border-2 border-border shadow-md' : ''}>
        <Heading level={4} className="mb-3">
          Group {groupName}
        </Heading>
        {hasWalkoverOrForfeit && (
          <Text className="text-sm text-info-dark bg-info-light/40 border border-info-dark/20 rounded-md px-3 py-2 mb-3">
            This group includes walkover(s) or forfeit(s). Rally points for those matches are counted as{' '}
            {walkoverPointsFor}–0 for the winner unless individual games were recorded.
          </Text>
        )}
        <div className="overflow-x-auto -mx-2 sm:mx-0">
          <div className="inline-block min-w-full align-middle px-2 sm:px-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-medium">
                  <th className="sticky left-0 z-10 bg-surface-muted text-left py-2 px-2 font-medium text-text-secondary" title="Position">#</th>
                  <th className="sticky left-8 z-10 bg-surface-muted text-left py-2 px-2 font-medium text-text-secondary min-w-[140px] sm:min-w-0">
                    {isSinglesTournament ? 'Player' : 'Team'}
                  </th>
                  <th className="text-center py-2 px-2 font-medium text-text-secondary whitespace-nowrap" title="Matches played">P</th>
                  <th className="text-center py-2 px-2 font-medium text-text-secondary whitespace-nowrap" title="Wins">W</th>
                  <th className="text-center py-2 px-2 font-medium text-text-secondary whitespace-nowrap" title="Losses">L</th>
                  <th className="text-center py-2 px-2 font-medium text-text-secondary whitespace-nowrap" title="Rally points for">PF</th>
                  <th className="text-center py-2 px-2 font-medium text-text-secondary whitespace-nowrap" title="Rally points against">PA</th>
                  <th
                    className="text-center py-2 px-2 font-medium text-text-secondary whitespace-nowrap"
                    title="Average rally points for per match"
                  >
                    Avg
                  </th>
                  <th className="text-center py-2 px-2 font-medium text-text-secondary whitespace-nowrap" title="Point difference (PF − PA)">+/-</th>
                </tr>
              </thead>
              <tbody>
                {standings.map((row) => {
                  const withdrawn = isTeamWithdrawn(row.team);
                  const isQualifying =
                    !withdrawn && teamsQualifyingPerGroup && row.position <= teamsQualifyingPerGroup;
                  return (
                    <tr
                      key={row.teamId}
                      className={`border-b border-border-subtle last:border-0 ${isQualifying ? 'bg-success/10 border-l-4 border-l-success' : ''}`}
                    >
                      <td className="sticky left-0 z-10 bg-surface-muted py-2 px-2 font-medium">{row.position}</td>
                      <td className="sticky left-8 z-10 bg-surface-muted py-2 px-2 min-w-[140px] sm:min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <Typography variant="body" className="text-sm">
                            {row.team?.player2 ? (
                              <>
                                <span className={getGenderTextColor(row.team?.player1?.gender || '')}>
                                  {formatPlayerName(row.team?.player1)}
                                </span>
                                {' & '}
                                <span className={getGenderTextColor(row.team?.player2?.gender || '')}>
                                  {formatPlayerName(row.team?.player2)}
                                </span>
                              </>
                            ) : (
                              <span className={getGenderTextColor(row.team?.player1?.gender || '')}>
                                {formatTournamentEntryLabel(row.team, true)}
                              </span>
                            )}
                          </Typography>
                          {withdrawn && (
                            <StatusBadge variant="inactive" size="small">
                              Withdrawn
                            </StatusBadge>
                          )}
                        </div>
                      </td>
                      <td className="py-2 px-2 text-center whitespace-nowrap">{row.matchesPlayed}</td>
                      <td className="py-2 px-2 text-center font-medium text-success whitespace-nowrap">{row.wins}</td>
                      <td className="py-2 px-2 text-center text-text-muted whitespace-nowrap">{row.losses}</td>
                      <td className="py-2 px-2 text-center whitespace-nowrap">{row.pointsFor}</td>
                      <td className="py-2 px-2 text-center whitespace-nowrap">{row.pointsAgainst}</td>
                      <td className="py-2 px-2 text-center text-text-secondary whitespace-nowrap">
                        {row.matchesPlayed > 0
                          ? (row.avgPointsPerMatch ?? row.pointsFor / row.matchesPlayed).toFixed(1)
                          : '—'}
                      </td>
                      <td
                        className={`py-2 px-2 text-center font-medium whitespace-nowrap ${row.pointDifference >= 0 ? 'text-success' : 'text-danger'}`}
                      >
                        {row.pointDifference >= 0 ? '+' : ''}
                        {row.pointDifference}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </Card>
    );
  }
);

GroupStandingsTable.displayName = 'GroupStandingsTable';

/**
 * League tables for all groups in a tournament
 */
const GroupStandingsTables = memo(
  ({
    groupStandings,
    teamsQualifyingPerGroup,
    inModal = false,
    rankingOrder,
    tiebreakerOrder,
    isSinglesTournament = false,
    walkoverPointsFor = 21,
  }) => {
    if (!groupStandings || groupStandings.length === 0) return null;

    const rulesText = formatRankingRulesForDisplay(rankingOrder, tiebreakerOrder);

    return (
      <div className="space-y-6">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-2">
          <Heading level={3} className="mb-0">
            Group Standings
          </Heading>
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-danger/10 border border-danger/30 text-danger text-xs font-medium">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-danger opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-danger" />
            </span>
            Live
          </span>
          {rulesText ? (
            <InfoHintButton
              text={rulesText}
              ariaLabel="How standings are ranked and tie-breakers are applied"
              maxWidth={320}
            />
          ) : null}
        </div>
        {teamsQualifyingPerGroup > 0 && (
          <Text className="text-text-secondary text-sm">
            Top {teamsQualifyingPerGroup} per group{' '}
            {teamsQualifyingPerGroup === 1 ? 'advances' : 'advance'}{' '}
            <span className="text-text-muted">· highlighted rows · withdrawn excluded</span>
          </Text>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {groupStandings.map(({ groupName, standings, hasWalkoverOrForfeit }) => (
            <GroupStandingsTable
              key={groupName}
              groupName={groupName}
              standings={standings}
              teamsQualifyingPerGroup={teamsQualifyingPerGroup}
              elevated={inModal}
              hasWalkoverOrForfeit={hasWalkoverOrForfeit}
              isSinglesTournament={isSinglesTournament}
              walkoverPointsFor={walkoverPointsFor}
            />
          ))}
        </div>
      </div>
    );
  }
);

GroupStandingsTables.displayName = 'GroupStandingsTables';

export default GroupStandingsTables;
